"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { updateSection } from "@/store/slices/webbuilderSlice";
import {
  getComponentClientMap,
  setComponentField,
} from "@team-shiphouse/shiphouse-themes/client";
import { setCurrentDevice } from "@/store/slices/ui.slice";
import { ChevronLeft, ChevronRight, Settings, Lock, Code } from "lucide-react";
import { ComponentDefinition } from "@/types/models/componentConfig";
import NestedPanel from "./NestedPanel";
import { SchemaSectionPropertiesPanel } from "./SchemaSectionPropertiesPanel";
import { sectionDataService, DeviceType } from "@/services/sectionDataService";
import { Button } from "@/components/ui/button";

interface SectionPropertiesPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  onDebugToggle: () => void;
  showMainContent?: boolean;
  componentDefinitions?: ComponentDefinition[];
  selectedSectionOverride?: any | null;
  isFullscreen?: boolean;
  showDeviceTabs?: boolean;
}

export function SectionPropertiesPanel({
  isOpen,
  onToggle,
  onDebugToggle,
  showMainContent = true,
  componentDefinitions = [],
  selectedSectionOverride = null,
  isFullscreen = false,
  showDeviceTabs = true,
}: SectionPropertiesPanelProps) {
  const dispatch = useDispatch();
  const panelContentRef = useRef<HTMLDivElement | null>(null);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastInitializedSectionIdRef = useRef<string | null>(null);
  const previousIsFullscreenRef = useRef(isFullscreen);
  const [formValues, setFormValues] = useState<{
    desktop: Record<string, unknown>;
    tablet: Record<string, unknown>;
    mobile: Record<string, unknown>;
  }>({ desktop: {}, tablet: {}, mobile: {} });
  const [selectedComponentKey, setSelectedComponentKey] = useState<
    string | null
  >(null);
  const [isWide, setIsWide] = useState(false);
  const [disableWidthTransition, setDisableWidthTransition] = useState(false);
  const [animateFullscreenEnter, setAnimateFullscreenEnter] = useState(false);

  const selectedSectionFromStore = useSelector((state: RootState) => {
    const wb = (state as any).webbuilder;
    if (!wb) return null;
    const id = wb.selectedSectionId;
    return id ? (wb.sectionsById?.[id] ?? null) : null;
  });

  const selectedSection =
    (selectedSectionOverride as any) ?? selectedSectionFromStore;
  const currentDevice = useSelector(
    (state: RootState) => state.ui.currentDevice,
  );
  const selectedSectionRegistryEntry = useMemo(() => {
    if (!selectedSection?.section_identifier) {
      return null;
    }

    const registry = getComponentClientMap();
    return registry[selectedSection.section_identifier] || null;
  }, [selectedSection?.section_identifier]);

  const sectionId = useMemo(
    () => selectedSection?.id || null,
    [selectedSection?.id],
  );
  const visibleComponentEntries = useMemo(() => {
    const componentData =
      selectedSection?.component_data ||
      selectedSectionRegistryEntry?.inputs ||
      {};

    if (selectedSection) {
      console.log("[SectionPropertiesPanel] Visible component entries:", {
        sectionId: selectedSection.id,
        sectionIdentifier: selectedSection.section_identifier,
        componentDataKeys: Object.keys(componentData || {}),
        entryCount: Object.keys(componentData || {}).length,
      });
    }

    return Object.entries(componentData) as Array<[string, any]>;
  }, [
    selectedSection?.component_data,
    selectedSectionRegistryEntry?.inputs,
    selectedSection?.section_identifier,
  ]);
  const defsCount = useMemo(
    () => componentDefinitions.length,
    [componentDefinitions.length],
  );
  const useWideLayout = isFullscreen || isWide;

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const wasFullscreen = previousIsFullscreenRef.current;
    previousIsFullscreenRef.current = isFullscreen;

    if (!wasFullscreen && isFullscreen) {
      setAnimateFullscreenEnter(true);
      const timeout = window.setTimeout(() => {
        setAnimateFullscreenEnter(false);
      }, 400);
      return () => window.clearTimeout(timeout);
    }

    if (wasFullscreen && !isFullscreen) {
      setDisableWidthTransition(true);
      const timeout = window.setTimeout(() => {
        setDisableWidthTransition(false);
      }, 50);
      return () => window.clearTimeout(timeout);
    }
  }, [isFullscreen]);

  useEffect(() => {
    if (!panelContentRef.current) return;

    const element = panelContentRef.current;
    const updateLayoutMode = () => {
      setIsWide(element.clientWidth > 600);
    };

    updateLayoutMode();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        updateLayoutMode();
      });
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateLayoutMode);
    return () => window.removeEventListener("resize", updateLayoutMode);
  }, [isFullscreen]);

  useEffect(() => {
    if (selectedSection && defsCount > 0 && sectionId) {
      const sectionChanged = lastInitializedSectionIdRef.current !== sectionId;

      if (sectionChanged || selectedComponentKey) {
        console.log("[SectionPropertiesPanel] Initializing form values:", {
          sectionId,
          sectionIdentifier: selectedSection.section_identifier,
          selectedComponentKey,
          defsCount,
        });
        const allDevices = ["desktop", "tablet", "mobile"] as DeviceType[];
        const initialValues = Object.fromEntries(
          allDevices.map((device) => {
            const deviceValues =
              sectionDataService.initializeFormValuesWithMapping(
                selectedSection,
                componentDefinitions,
                device,
                selectedComponentKey || undefined,
              );
            return [device, deviceValues];
          }),
        );

        setFormValues(initialValues as typeof formValues);

        console.log("[SectionPropertiesPanel] Initial form values initialized:", {
          sectionId,
          sectionIdentifier: selectedSection.section_identifier,
          devices: Object.keys(initialValues),
          desktopKeys: Object.keys((initialValues as any).desktop || {}),
          tabletKeys: Object.keys((initialValues as any).tablet || {}),
          mobileKeys: Object.keys((initialValues as any).mobile || {}),
        });

        if (sectionChanged) {
          lastInitializedSectionIdRef.current = sectionId;
        }
      }
    } else if (!selectedSection && lastInitializedSectionIdRef.current) {
      setFormValues({ desktop: {}, tablet: {}, mobile: {} });
      lastInitializedSectionIdRef.current = null;
    }
  }, [
    selectedSection,
    defsCount,
    sectionId,
    selectedComponentKey,
    componentDefinitions,
  ]);

  useEffect(() => {
    if (selectedSection && selectedComponentKey) {
      const currentComponentExists = visibleComponentEntries.some(
        ([key]) => key === selectedComponentKey,
      );
      console.log("[SectionPropertiesPanel] Selected component check:", {
        sectionId: selectedSection.id,
        selectedComponentKey,
        currentComponentExists,
        visibleKeys: visibleComponentEntries.map(([key]) => key),
      });
      if (!currentComponentExists) {
        console.warn(
          "[SectionPropertiesPanel] Selected component missing from visible entries; clearing selection:",
          {
            sectionId: selectedSection.id,
            selectedComponentKey,
            visibleKeys: visibleComponentEntries.map(([key]) => key),
          },
        );
        setSelectedComponentKey(null);
      }
    } else if (!selectedSection) {
      setSelectedComponentKey(null);
    }
  }, [selectedSection, selectedComponentKey, visibleComponentEntries]);

  const handleInputChange = (fieldKey: string, value: unknown) => {
    setFormValues((prev) => ({
      ...prev,
      [currentDevice]: {
        ...prev[currentDevice],
        [fieldKey]: value,
      },
    }));

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      if (selectedSection) {
        const updatedSection = sectionDataService.updateFormValueWithMapping(
          selectedSection,
          componentDefinitions,
          fieldKey,
          value,
          currentDevice,
          selectedComponentKey || undefined,
        );

        dispatch(updateSection(updatedSection));

        try {
          if (selectedComponentKey) {
            const configTitle = fieldKey.split("::")[1] || fieldKey;
            (setComponentField as any)(
              updatedSection.id,
              selectedComponentKey,
              configTitle,
              value,
              currentDevice,
            );
          }
        } catch (error) {
          console.warn(
            "Failed to set component field via componentDataStore",
            error,
          );
        }
      }
    }, 300);
  };

  const handleDeviceChange = (newDevice: DeviceType) => {
    dispatch(setCurrentDevice(newDevice));
  };

  const handleComponentClick = (componentKey: string) => {
    console.log("[SectionPropertiesPanel] Component tile clicked:", {
      sectionId: selectedSection?.id,
      sectionIdentifier: selectedSection?.section_identifier,
      componentKey,
    });
    setSelectedComponentKey(componentKey);
  };

  const handleBackClick = () => {
    setSelectedComponentKey(null);
  };

  return (
    <div
      className={`relative h-full shrink-0 ${
        isFullscreen ? "flex-1 min-w-0" : ""
      } ${animateFullscreenEnter ? "animate-panelExpandFromRight" : ""} ${
        isFullscreen ? "" : "transition-opacity duration-500 ease-out"
      } ${showMainContent ? "opacity-100" : "opacity-0"}`}
    >
      {!isFullscreen && (
        <button
          onClick={onToggle}
          className="absolute top-20 right-full z-20 w-8 h-12 bg-card dark:bg-auth-card border-l border-t border-b border-border dark:border-auth-background rounded-l-lg flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors shadow-theme-lg"
          aria-label={
            isOpen ? "Close properties panel" : "Open properties panel"
          }
          style={{ marginRight: isOpen ? "-4px" : "0px" }}
        >
          {isOpen ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      )}

      <div
        className={`${
          isFullscreen ? "flex-1 min-w-0 w-full" : isOpen ? "w-64" : "w-0"
        } h-full bg-card dark:bg-auth-card border-l border-border dark:border-auth-background flex flex-col rounded-l-3xl ${isFullscreen || disableWidthTransition ? "" : "transition-[width] duration-300"} overflow-hidden relative`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        data-form-panel="true"
      >
        <div
          ref={panelContentRef}
          className={`${isFullscreen ? "w-full min-w-0" : "w-64"} flex flex-col h-full`}
        >
          <div className="p-4 border-b border-border dark:border-auth-background">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Section Properties</h2>
              <Button
                variant="outline"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onDebugToggle();
                }}
              >
                <Code className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden relative">
            {selectedSectionRegistryEntry?.schema && selectedSection ? (
              <SchemaSectionPropertiesPanel
                schema={selectedSectionRegistryEntry.schema}
                defaultInputs={selectedSectionRegistryEntry.defaultInputs}
                selectedSection={selectedSection}
                currentDevice={currentDevice}
                isLocked={selectedSection?.is_locked || false}
              />
            ) : !selectedComponentKey ? (
              <div className="h-full p-4 overflow-y-auto">
                {selectedSection ? (
                  useWideLayout ? (
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                      {selectedSection.is_locked && (
                        <div className="col-span-full p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Lock className="w-4 h-4 text-amber-600" />
                            <p className="text-sm text-amber-800 dark:text-amber-200">
                              Section is locked. Unlock it to edit properties.
                            </p>
                          </div>
                        </div>
                      )}

                      {visibleComponentEntries.map(
                        ([key, value]) => (
                          <button
                            key={key}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!selectedSection.is_locked) {
                                handleComponentClick(key);
                              }
                            }}
                            disabled={selectedSection.is_locked}
                            className={`p-4 border border-border rounded-lg transition-colors text-left flex flex-col justify-between min-h-32 ${
                              selectedSection.is_locked
                                ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800"
                                : "hover:shadow-lg hover:scale-[1.01]"
                            }`}
                          >
                            <div>
                              <div className="text-sm font-semibold mb-2">
                                {value.name}
                              </div>
                              <div className="text-xs text-muted-foreground line-clamp-3">
                                {value.description ||
                                  value.componentSlug ||
                                  "Component"}
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground self-end">
                              <ChevronRight className="w-4 h-4 inline" />
                            </div>
                          </button>
                        ),
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedSection.is_locked && (
                        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Lock className="w-4 h-4 text-amber-600" />
                            <p className="text-sm text-amber-800 dark:text-amber-200">
                              Section is locked. Unlock it to edit properties.
                            </p>
                          </div>
                        </div>
                      )}

                      {visibleComponentEntries.map(
                        ([key, value]) => (
                          <button
                            key={key}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!selectedSection.is_locked) {
                                handleComponentClick(key);
                              }
                            }}
                            disabled={selectedSection.is_locked}
                            className={`w-full flex items-center justify-between p-3 border border-border rounded-lg transition-colors ${
                              selectedSection.is_locked
                                ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800"
                                : "hover:bg-muted dark:hover:bg-primary/20"
                            }`}
                          >
                            <span className="font-semibold text-sm">
                              {value.name}
                            </span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </button>
                        ),
                      )}
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center mb-3">
                      <Settings className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">
                        No section selected
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Click on a section in the canvas to view its properties
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NestedPanel
                componentKey={selectedComponentKey}
                componentData={
                  selectedComponentKey
                    ? (selectedSection?.component_data ||
                        selectedSectionRegistryEntry?.inputs ||
                        {})?.[selectedComponentKey]
                    : undefined
                }
                componentDefinitions={componentDefinitions}
                onBack={handleBackClick}
                formValues={formValues[currentDevice] || {}}
                onInputChange={handleInputChange}
                currentDevice={currentDevice}
                onDeviceChange={handleDeviceChange}
                isLocked={selectedSection?.is_locked || false}
                layoutMode={useWideLayout ? "grid" : "vertical"}
                showDeviceTabs={showDeviceTabs}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
